import {
  Component,
  ChangeDetectorRef,
  computed,
  inject,
  input,
  signal,
  effect,
  OnDestroy,
  output,
  OnInit,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClarityModule } from '@clr/angular';
import { APIService } from 'src/app/services/APIService/api.service';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { Table } from 'src/app/models/ui/table';
import { PlotlyPlotComponent } from '../../plotly-plot/plotly-plot.component';
import { PlotlyHTMLElement, relayout } from 'plotly.js-basic-dist-min';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-trassierung',
  imports: [PlotlyPlotComponent, CommonModule, ClarityModule, FormsModule, MrTranslatePipe],
  templateUrl: './trassierung.html',
  styleUrl: './trassierung.scss',
})
export class TrassierungComponent implements OnInit {
  private mrTranslate = inject(MrTranslatePipe);
  public readonly ostammid = input.required<number>();
  public readonly trassierungtable = input.required<Table>();
  //public readonly name = input.required<string>();
  public readonly baseStartMeter = input.required<number>();
  public readonly baseEndMeter = input.required<number>();
  public readonly layout = signal<any>({});
  public readonly graphconfig = signal<any>({});
  public readonly plots = signal<TrassierungPlot[]>([]);
  protected readonly resize$ = input<Subject<void>>(undefined);

  // private readonly changeDet = inject(ChangeDetectorRef);

  kstedt = signal([
    { id: 1, bezeichnung: 'Radius' },
    { id: 2, bezeichnung: 'Überhöhung' },
    { id: 6, bezeichnung: 'Neigung' },
    { id: 3, bezeichnung: 'Geschwindigkeit' },
  ]);

  constructor() {
    // Auto-load first trassierung when data arrives
    effect(() => {
      const plots: TrassierungPlot[] = [];

      const data = this.trassierungtable();

      if (data.rows.length === 0) return;

      const result = this.kstedt().map(k => ({
        bezeichnung: k.bezeichnung,
        werte: data.rows
          .filter(d => d.KSTEDTID === k.id)
          .map(({ Startposition, Endposition, Anfangswert, Endwert, Steigung, Radius }) => ({
            Startposition,
            Endposition,
            Anfangswert,
            Endwert,
            Steigung,
            Radius,
          })),
      }));

      console.log(result);

      let tmpstart = this.baseStartMeter;
      let tmpende = this.baseEndMeter;
      let richtung = 0;
      if (this.baseStartMeter > this.baseEndMeter) richtung = 1;

      result.forEach(k => {
        let x = [];
        let y = [];
        let lastX;
        let lastY;
        let i = 0;

        k.werte.forEach(w => {
          let s = w.Startposition;
          let e = w.Endposition;
          let aw = w.Anfangswert;
          let ew = w.Endwert;

          if (i == 0) {
            if (s > tmpstart) {
              x.push(tmpstart);
              y.push(0);
            }
            lastX = s;
          }

          if (lastX < s) {
            x.push(lastX);
            y.push(0);

            x.push(s);
            y.push(0);
          }

          if (richtung == 1) {
            x.push(e);
            y.push(aw);
            x.push(s);
            y.push(ew);
          } else {
            x.push(s);
            y.push(aw);
            x.push(e);
            y.push(ew);
          }

          if (i == k.werte.length - 1) {
            if (e < tmpende) {
              x.push(e);
              y.push(0);
              x.push(tmpende);
              y.push(0);
            }
          }

          lastX = e;
          i++;
        });

        for (let i = 1; i < x.length; i++) {
          let tx0 = x[i - 1];
          let tx1 = x[i];
          let ty0 = y[i - 1];
          let ty1 = y[i];

          if (tx0 == tx1 && ty0 == ty1) {
            x.splice(i, 1);
            y.splice(i, 1);
          }
        }

        if (k.bezeichnung == 'Neigung') {
          let xm = [];
          let ym = [];
          let tx = [];
          k.werte.forEach(w => {
            let s: number = w.Startposition as number;
            let e: number = w.Endposition as number;
            let aw: number = w.Anfangswert as number;
            let ew: number = w.Endwert as number;

            if (richtung == 1) {
              s = w.Endposition as number;
              e = w.Startposition as number;
            }

            let _xm = (e + s) / 2;
            let _ym = (aw + ew) / 2;

            xm.push(_xm);
            ym.push(_ym);
            tx.push((w.Steigung as number).toFixed(2) + ' ‰');
          });

          let xt = [];
          let yt = [];
          let tt = [];

          for (let i = 1; i < k.werte.length; i++) {
            let row0 = k.werte[i - 1];
            let row1 = k.werte[i];
            let aw: number = row1['Anfangswert'] as number;
            let s: number = row1['Startposition'] as number;
            if (richtung == 1) {
              s = row1['Endposition'] as number;
            }
            let stg0: number = row0['Steigung'] as number;
            let stg1: number = row1['Steigung'] as number;
            let ar: number = row1['Radius'] as number;

            let stg = stg0 / 10 - stg1 / 10;
            let t = Math.abs(((ar / 2) * stg) / 100);
            let f = (t * t) / (2 * ar);
            let h = Math.abs((row0['Endwert'] as number) - (row0['Anfangswert'] as number));
            let l = Math.abs((row0['Endposition'] as number) - (row0['Startposition'] as number));

            let alpha1 = Math.atan(h / l);
            let _ba = Math.tan(alpha1) * t;

            h = Math.abs((row1['Endwert'] as number) - (row1['Anfangswert'] as number));
            l = Math.abs((row1['Endposition'] as number) - (row1['Startposition'] as number));

            let alpha2 = Math.atan(h / l);
            let _be = Math.tan(alpha2) * t;

            let BaX = s - t;
            let BeX = s + t;
            let BaY = aw - _ba;
            let BeY = aw - _be;
            let stHW = 'HW';

            if (stg0 > 0 && stg1 < 0) {
              BaY = aw - _ba;
              BeY = aw - _be;
              stHW = 'HK';
            }

            if (stg0 <= 0 && stg1 > 0) {
              BaY = aw + _ba;
              BeY = aw + _be;
              stHW = 'HW';
            }

            if (stg0 < 0 && stg1 < 0) {
              BaY = aw + _ba;
              BeY = aw - _be;
              stHW = 'HW';

              if (stg0 > stg1) stHW = 'HK';
            }

            if (stg0 < 0 && stg1 == 0) {
              BaY = aw + _ba;
              BeY = aw - _be;
              stHW = 'HW';
            }

            if (stg0 > 0 && stg1 > 0) {
              BaY = aw - _ba;
              BeY = aw + _be;
              stHW = 'HW';

              if (stg0 > stg1) stHW = 'HK';
            }

            xt.push(BaX);
            yt.push(BaY);

            xt.push(BeX);
            yt.push(BeY);

            let strLbl = BeX.toFixed(3) + '<br>';
            strLbl += BeY.toFixed(3) + '<br>';
            strLbl += stHW + ' = ' + ar.toFixed(0) + '<br>';
            strLbl += 'T = ' + t.toFixed(3) + '<br>';
            strLbl += 'f = ' + f.toFixed(3) + '<br>';
            tt.push(strLbl);
          }

          let newtt = [];
          if (k.werte.length > 1) {
            let row0 = k.werte[0];
            let aw: number = row0['Anfangswert'] as number;

            newtt.push(aw.toFixed(3) + '<br>');
            for (const element of tt) {
              newtt.push(element);
            }
            row0 = k.werte.at(-1);
            aw = row0['Endwert'] as number;
            newtt.push(aw.toFixed(3) + '<br>');
          }

          let trace1 = {
            x: x,
            y: y,
            mode: 'lines+markers+text',
            name: k.bezeichnung,
            line: {
              color: 'rgb(60, 133, 0)',
            },
            text: newtt,
            textposition: 'top',
            marker: {
              color: 'rgb(0, 0, 0)',
              size: 10,
            },
          };

          let traceT = {
            x: xt,
            y: yt,
            mode: 'markers',
            name: k.bezeichnung + 'mittett',
            marker: {
              color: 'rgb(0, 0, 0)',
              symbol: 'diamond',
              size: 6,
            },
          };

          let traceM = {
            x: xm,
            y: ym,
            mode: 'text',
            name: k.bezeichnung + 'mitte',
            text: tx,
            textposition: 'top',
          };

          if (x.length > 0) {
            const values = [...trace1.y];
            const min = Math.min(...values);
            const max = Math.max(...values);
            const layout = this.layout();

            plots.push({
              show: true,
              title: k.bezeichnung,
              data: [trace1, traceM, traceT],
              layout: {
                ...layout,
                title: k.bezeichnung,
                yaxis: {
                  ...this.layout().yaxis,
                  range: [min - 5, max + 5],
                },
              },
              el: null,
            });
          }
        } else {
          let trace = {
            x: x,
            y: y,
            type: 'scattergl',
            mode: 'lines+markers+text',
            text: y.map(y => y.toFixed(1)),
            textposition: 'top',
            name: k.bezeichnung,
            line: {
              color: 'rgb(60, 133, 0)',
            },
          };

          if (x.length > 0) {
            plots.push({
              show: true,
              title: k.bezeichnung,
              data: [trace],
              layout: {
                ...this.layout(),
                title: k.bezeichnung,
              },
              el: null,
            });
          }
        }
      });

      this.plots.set(plots);
    });
  }

  applyYaxis(el: PlotlyHTMLElement) {
    relayout(el, {
      'yaxis.autorange': false,
      'yaxis.range[0]': el.layout.yaxis.range[0],
      'yaxis.range[1]': el.layout.yaxis.range[1],
    });
  }

  getGraphIndex(key: string): number {
    for (let i = 0; i < this.plots().length; i++) {
      if (this.plots()[i].title == key) {
        return i;
      }
    }
    return undefined;
  }

  ngOnInit() {
    this.plots.set([]);
    this.layout.set({
      autosize: true,
      hovermode: 'closest',
      showlegend: false,
      legend: { orientation: 'h' },
      height: 300,
      margin: {
        l: 40,
        r: 20,
        b: 25,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
    });

    this.graphconfig.set({
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'lasso2d',
        'zoomOut2d',
        'zoomIn2d',
        'autoScale2d',
        'toggleSpikelines',
        'hoverCompareCartesian',
        'hoverClosestCartesian',
      ],
    });
  }
}

export interface TrassierungPlot {
  show: boolean;
  title: string;
  data: any[];
  layout: any;
  el: PlotlyHTMLElement;
}
